// @flow

import * as React from 'react'
import CircularProgress from 'material-ui/CircularProgress'
import cata from '@vimcar/shared-utils/cata'
import {
  Page,
  DocumentTitle,
  HeaderTitle,
} from '@vimcar/shared-components/components/AppLayout'
import Button from '@vimcar/shared-components/components/Button'
import Icon from '@vimcar/shared-components/components/Icon'
import MatchMedia from '@vimcar/shared-components/components/MatchMedia'
import Menu from '@vimcar/shared-components/components/Menu'
import TabButton from '@vimcar/shared-components/components/TabButton'
import TableCard from '@vimcar/shared-components/components/TableCard'
import TextField from '@vimcar/shared-components/components/TextField'
import linkViewModel from '@vimcar/shared-utils/linkViewModel'
import { sortable } from '@vimcar/shared-components/components/_DEPRECATED_/DataTable/plugins/sortable'
import FullscreenMessage from '@vimcar/shared-components/components/FullscreenMessage'

import BookingDialog from '#components/BookingDialog'
import Err from '#components/Err'
import WalkthroughWrapper from '#components/WalkthroughWrapper'
import { menuable } from '#utils/menuable'
import i18n from '#utils/i18n'
import {
  BREAKPOINT_MOBILE,
  BREAKPOINT_DESKTOP,
} from '#utils/ui'
import mkViewModel, { type ViewModel } from './viewModel'
import styles from './styles.css'

const tp = i18n.prefix('components.BookingsOverview')

type Props = {
  viewModel: ViewModel,
  loading: ?boolean,
}

const BookingsOverview = ({ viewModel, loading }: Props) => {
  const content = cata({
    initializing: () => null,
    initialized: () => (
      <React.Fragment>
        <MatchMedia query={BREAKPOINT_MOBILE}>
          <div className={styles.filters}>
            <Menu
              className={styles.dropdown}
              menuItems={[{
                isRoot: true,
                primaryText: (
                  <Button
                    raised
                    className={styles.button}
                    >
                    {viewModel.activeFilterLabel}
                    <Icon icon='arrow-drop-down'/>
                  </Button>
                ),
                menuItems: viewModel.filterActions
                  .map(({ label, onClick }) => ({
                    primaryText: label,
                    onClick,
                  })),
              }]}
              />
          </div>
        </MatchMedia>

        <MatchMedia query={BREAKPOINT_DESKTOP}>
          <div className={styles.tabButtonGroup}>
            {viewModel.filterActions.map(({ active, label, onClick }) => (
              <TabButton
                active={active}
                className={styles.tabButton}
                icon='calendar'
                key={label}
                onClick={onClick}
                >
                {label}
              </TabButton>
            ))}
          </div>
        </MatchMedia>

        <TableCard
          searchable
          actions={[{
            children: loading && (
              <CircularProgress
                className={styles.progress}
                size={20}
                thickness={2}
                />
            ),
          }]}
          className={styles.table}
          columns={viewModel.columns}
          filterPaths={viewModel.filterPaths}
          header={viewModel.error && (<Err>{viewModel.error}</Err>)}
          plugins={[
            // @TODO: plugins should only be initialized once
            sortable(),
            menuable(viewModel.menuablePluginOptions(styles)),
          ]}
          rows={viewModel.rows}
          title={tp('bookings')}
          />

        {viewModel.showEndBookingDialog && viewModel.currentBooking && (
          <BookingDialog
            // $FlowFixMe: currentBooking is not null
            end={viewModel.currentBooking.formattedEnd}
            // $FlowFixMe: currentBooking is not null
            licenseplate={viewModel.currentBooking.licenseplate}
            // $FlowFixMe: currentBooking is not null
            makeModel={viewModel.currentBooking.makeModel}
            onPrimary={viewModel.handleConfirmEndBooking}
            onSecondary={viewModel.handleCancelEndBooking}
            primaryLabel={tp('end-booking')}
            primaryProps={{ error: true, raised: false }}
            // $FlowFixMe: currentBooking is not null
            start={viewModel.currentBooking.formattedStart}
            title={tp('end-booking-title')}
            >
            <p className={styles.center}>
              {tp('confirm-end-text')}
            </p>
          </BookingDialog>
        )}

        {viewModel.showDeleteBookingDialog && viewModel.currentBooking && (
          <BookingDialog
            // $FlowFixMe: currentBooking is not null
            end={viewModel.currentBooking.formattedEnd}
            // $FlowFixMe: currentBooking is not null
            licenseplate={viewModel.currentBooking.licenseplate}
            // $FlowFixMe: currentBooking is not null
            makeModel={viewModel.currentBooking.makeModel}
            onPrimary={viewModel.handleConfirmDeleteBooking}
            onSecondary={viewModel.handleCancelDeleteBooking}
            primaryLabel={tp('cancel-booking')}
            primaryProps={{ error: true, raised: false }}
            // $FlowFixMe: currentBooking is not null
            start={viewModel.currentBooking.formattedStart}
            title={tp('delete-booking')}
            >
            <TextField
              readOnly
              className={styles.field}
              floatingLabelText={tp('driver')}
              // $FlowFixMe: currentBooking is not null
              value={viewModel.currentBooking.name}
              />
            <p className={styles.center}>
              {tp('confirm-delete-text')}
            </p>
          </BookingDialog>
        )}

        <WalkthroughWrapper
          onCallback={viewModel.handleWalkThroughClose}
          run={viewModel.showWalkThrough}
          walkThroughSteps={viewModel.walkThroughSteps}
          />
      </React.Fragment>
    ),
    error: () => <FullscreenMessage title={tp('loading-failed')}/>,
  })(viewModel.initializingState || 'error')

  return viewModel.isKioskMode === false
    ? (
      <React.Fragment>
        <DocumentTitle>{tp('my-bookings')} | Vimcar</DocumentTitle>
        <HeaderTitle>{tp('my-bookings')}</HeaderTitle>
        <Page loading={viewModel.initializingState === 'initializing'}>
          {content}
        </Page>
      </React.Fragment>
    )
    : content
}

export default linkViewModel<*, *>(mkViewModel, BookingsOverview)
